import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Package, Plus } from 'lucide-react'
import { ConsumerShell } from '@/consumer/components/consumer-shell'
import { ProductCard } from '@/consumer/components/product-card'
import { Button } from '@/shared/components/ui/button'
import { useConsumerHoldings } from '@/consumer/hooks/use-holdings'
import { useSelfRegistrationEnabled } from '@/consumer/hooks/use-self-registration'

/**
 * Full list of the consumer's registered products. The homepage (`/`) shows
 * only the first few as an overview; this page (`/products`) is the "See all"
 * destination and reuses the same `ProductCard`.
 */
export default function ProductsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: holdings, isPending } = useConsumerHoldings()
  // WPS-138 #1: hide the "Register Product" CTAs for tenants that do not offer
  // consumer self-registration (OC at go-live).
  const selfRegistrationEnabled = useSelfRegistrationEnabled()

  return (
    <ConsumerShell>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="clara-title mb-1">My Products</h1>
          <p className="text-[12px] text-[var(--ink-3)]">
            {holdings ? `${holdings.length} registered product${holdings.length !== 1 ? 's' : ''}` : '…'}
          </p>
        </div>
        {selfRegistrationEnabled && (
          <Button onClick={() => navigate('/register')} className="cursor-pointer">
            <Plus className="h-3.5 w-3.5" />
            Register Product
          </Button>
        )}
      </div>
      {/* Loading */}
      {isPending && (
        <div className="flex items-center justify-center py-20">
          <p className="text-[13px] text-[var(--ink-3)]">Loading…</p>
        </div>
      )}
      {/* Product cards grid */}
      {!isPending && holdings && holdings.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {holdings.map((h) => (
            <ProductCard
              key={h.uuid}
              holding={h}
              onClick={() => navigate(`/products/${h.uuid}`)}
            />
          ))}
        </div>
      )}
      {/* Empty state */}
      {!isPending && holdings && holdings.length === 0 && (
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-16 text-center">
          <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--bg-subtle)]">
            <Package className="h-7 w-7 text-[var(--ink-3)]" />
          </div>
          <h2 className="clara-heading mb-2">No products yet</h2>
          <p className="mx-auto mb-6 max-w-xs text-[12px] text-[var(--ink-3)]">
            When a business registers a warranty for you, it will appear here.
            {selfRegistrationEnabled
              ? t('products.noProductsSelfRegHint', ' You can also register products yourself.')
              : ''}
          </p>
          {selfRegistrationEnabled && (
            <Button onClick={() => navigate('/register')} className="cursor-pointer">
              <Plus className="h-3.5 w-3.5" />
              Register your first product
            </Button>
          )}
        </div>
      )}
    </ConsumerShell>
  );
}
